import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../../components/ui/Header';
import Footer from '../homepage/components/Footer';
import Input from '../../components/ui/Input';
import Button from '../../components/ui/Button';
import Icon from '../../components/ui/Icon';
import { supabase } from '../../lib/supabase';
import { userService } from '../../services/userService';
import { useAuth } from '../../contexts/AuthContext';

export default function Signup() {
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [loading, setLoading] = useState(false);
  const [showAgreement, setShowAgreement] = useState(false);
  const [agreementText, setAgreementText] = useState('');
  const [agreementVersionId, setAgreementVersionId] = useState(null);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [emailConfirmationRequired, setEmailConfirmationRequired] = useState(false);

  const [agreedToTerms, setAgreedToTerms] = useState(false);

  // Symmetrical Already-Authenticated Redirect:
  useEffect(() => {
    if (!authLoading && user) {
      navigate('/model-catalog');
    }
  }, [user, authLoading, navigate]);

  // We fetch terms on mount to have them ready for the checkbox
  useEffect(() => {
    const fetchTerms = async () => {
      const { data } = await supabase
        .from('seller_agreements_versions')
        .select('*')
        .eq('agreement_type', 'buyer')
        .eq('is_active', true)
        .order('created_at', { ascending: false })
        .limit(1)
        .maybeSingle();

      if (data) {
         setAgreementText(data.content);
         setAgreementVersionId(data.id);
      }
    };
    fetchTerms();
  }, []);

  const handleSignup = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setEmailConfirmationRequired(false);
    setLoading(true);

    if (password !== confirmPassword) {
      setError('Passwords do not match.');
      setLoading(false);
      return;
    }

    if (!agreedToTerms && agreementVersionId) {
      setError('You must agree to the terms and conditions to create an account.');
      setLoading(false);
      return;
    }

    try {
      console.log('Signup - Starting flow for email:', email);
      // Check if email already exists using RPC function
      const { data: exists, error: checkError } = await supabase
        .rpc('check_email_exists', { email_to_check: email });

      if (checkError) {
        console.error('Signup - Error checking existing email:', checkError);
      } else if (exists) {
        console.log('Signup - Email already exists:', email);
        setError('An account already exists with that email. Please choose a different email or request a password reset.');
        setLoading(false);
        return;
      }

      console.log('Signup - Proceeding to Supabase Auth signUp for:', email);
      
      // Pass the agreement ID in metadata so the DB trigger can record it automatically
      const { data: signupData, error: signupError } = await supabase?.auth?.signUp({
        email: email,
        password: password,
        options: {
          data: {
            first_name: firstName,
            last_name: lastName,
            agreement_version_id: agreementVersionId
          }
        }
      });

      if (signupError) {
        console.error('Signup - Auth error detailed:', signupError);
        throw new Error(signupError?.message || 'Failed to create account.');
      }

      const newUserId = signupData?.user?.id;

      if (!newUserId) {
        setEmailConfirmationRequired(true);
        setSuccess('Confirmation email sent! Please check your inbox to activate your account.');
        setLoading(false);
        return;
      }

      console.log('Signup - Authentication successful:', { userId: newUserId });

      // The database trigger 'handle_new_user' now automatically creates the 
      // public.users profile and records the seller_agreement using the 
      // metadata we passed in the signUp call above.
      
      // Record the initial signup activity to capture geo/device data for the new account
      await userService.recordActivity(newUserId, navigator.userAgent, 'Signup', 'Account Created').catch(e => console.error('Activity track error:', e));

      if (signupData?.session) {
        navigate('/user-account-dashboard', { replace: true });
      } else {
        setEmailConfirmationRequired(true);
        setSuccess('Confirmation email sent! Please check your inbox to activate your account.');
      }

    } catch (err) {
      console.error('Signup error:', err);
      setError(err?.message || 'Failed to create account.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Header />
      <div className="min-h-screen bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-md mx-auto">
          <div className="bg-white rounded-lg shadow-md p-8">
            <div className="text-center mb-8">
              <h2 className="text-3xl font-bold text-gray-900">Create Your Account</h2>
              <p className="text-gray-600 mt-2">Sign up to get started</p>
            </div>

            {error && (
              <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg text-red-700">
                <div className="flex items-start">
                  <Icon name="AlertCircle" size={20} className="mr-2 flex-shrink-0 mt-0.5" />
                  <span className="text-sm">{error}</span>
                </div>
              </div>
            )}

            {success && (
              <div className="mb-6 p-5 bg-blue-50 border border-blue-200 rounded-lg text-blue-800 shadow-sm">
                <div className="flex flex-col items-center text-center">
                  <Icon name="Mail" size={32} className="mb-3 text-blue-500" />
                  <h3 className="text-lg font-semibold mb-1">Check Your Email</h3>
                  <span className="text-sm">{success}</span>
                </div>
              </div>
            )}

            {!emailConfirmationRequired && (
              <form onSubmit={handleSignup} className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <Input
                    label="First Name"
                    value={firstName}
                    onChange={(e) => setFirstName(e.target.value)}
                    required
                    placeholder="First"
                  />
                  <Input
                    label="Last Name"
                    value={lastName}
                    onChange={(e) => setLastName(e.target.value)}
                    required
                    placeholder="Last"
                  />
                </div>
                <Input
                  label="Email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  placeholder="your.email@example.com"
                />
                <Input
                  label="Password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                />
                <Input
                  label="Confirm Password"
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  required
                />

                {agreementText && (
                  <div className="flex items-start mt-4 mb-4">
                    <div className="flex items-center h-5">
                      <input
                        id="terms"
                        type="checkbox"
                        className="w-4 h-4 text-blue-600 bg-white border-gray-300 rounded focus:ring-blue-500 cursor-pointer"
                        checked={agreedToTerms}
                        onChange={(e) => setAgreedToTerms(e.target.checked)}
                      />
                    </div>
                    <div className="ml-3 text-sm">
                      <label htmlFor="terms" className="font-medium text-gray-700 cursor-pointer">
                        By signing up and using our website you have{' '}
                        <button 
                          type="button" 
                          onClick={() => setShowAgreement(true)} 
                          className="text-blue-600 hover:text-blue-800 underline"
                        >
                          read and agree
                        </button>
                        {' '}to the OpenGadget terms and conditions.
                      </label>
                    </div>
                  </div>
                )}

                <Button
                  type="submit"
                  disabled={loading || (agreementText && !agreedToTerms)}
                  className="w-full"
                  iconName={loading ? "Loader2" : "UserPlus"}
                  iconClassName={loading ? "animate-spin" : ""}
                  iconPosition="right"
                >
                  {loading ? 'Signing Up...' : 'Sign Up'}
                </Button>
              </form>
            )}

            <div className="mt-6 text-center">
              <p className="text-sm text-gray-600">
                Already have an account?{' '}
                <button
                  type="button"
                  onClick={() => navigate('/signin')}
                  className="text-blue-600 hover:text-blue-700 font-medium"
                >
                  Sign In
                </button>
              </p>
            </div>
          </div>
        </div>
      </div>
      <Footer />

      {showAgreement && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4">
          <div className="bg-white rounded-lg p-6 max-w-4xl w-full flex flex-col max-h-[90vh] shadow-2xl">
            <div className="flex justify-between items-center mb-4">
               <h2 className="text-2xl font-bold text-gray-900">OpenGadget Terms & Conditions</h2>
               <button onClick={() => setShowAgreement(false)} className="text-gray-400 hover:text-gray-600 transition-colors">
                 <Icon name="X" size={28} />
               </button>
            </div>
            <div 
              className="flex-1 overflow-y-auto p-6 bg-gray-50 border border-gray-200 rounded-md whitespace-pre-wrap text-sm text-gray-700 prose max-w-none"
              dangerouslySetInnerHTML={{ __html: agreementText }}
            />
            <div className="mt-6 flex justify-end gap-3">
              <Button onClick={() => setShowAgreement(false)} className="px-6">Close</Button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
